import { Injectable } from '@angular/core';
import { defer, Observable, Subject } from 'rxjs';
import CustomStore from 'devextreme/data/custom_store';
import { HhsgSharedApplication } from '../models/application.model';
import { HhsgSharedClient } from '../models/client.model';
import { HhsgSharedHttpService } from './http.service';
import { HhsgSharedConfigService } from './config.service';
import { User } from '../models/user.model';
import { HhsgSharedCorporationClientApplicationInfo } from '../models/corporation-client-application-info.model';
import { HhsgSharedTimezone } from '../models/timezone.model';
import { HhsgSharedAspDataStoreService } from './asp-data-store.service';
import { HhsgSharedEllipseSelectOption } from '../models';


@Injectable({
	providedIn: 'root'
})
export class HhsgSharedClientAdminService {
    private readonly _apiServer: string;
    private readonly _controllerName: string = 'ClientAdmin';

	public activeClient:HhsgSharedClient;
	public activeClientSource = new Subject<HhsgSharedClient>();
    public activeClientSource$ = this.activeClientSource.asObservable();
	private clientAdvancedFiltersClickedSource = new Subject<HhsgSharedEllipseSelectOption>();
    public clientAdvancedFiltersClickedSource$ = this.clientAdvancedFiltersClickedSource.asObservable();


    public static returnUrl: string;
	public static currentClient: HhsgSharedClient;

	constructor(    
        private _aspDataStoreService: HhsgSharedAspDataStoreService,
        private _configService: HhsgSharedConfigService,
        private _httpService: HhsgSharedHttpService) { 
        this._apiServer = _configService.appSettings.stsServer;
    }

    public async getAllActiveApplications(): Promise<HhsgSharedApplication[]> {
		return this._httpService.doGetRequestAsync<HhsgSharedApplication[]>(this._apiServer, this._controllerName, 'GetAllActiveApplications');
	}

    public async getAllActiveClients(applicationAbbreviation:string): Promise<HhsgSharedClient[]> {
		return await this._httpService.doGetRequestAsync<HhsgSharedClient[]>(this._apiServer, this._controllerName,
			`GetAllClients/${applicationAbbreviation}`);
	}

    public async getAllApplications(): Promise<HhsgSharedApplication[]> {
		return this._httpService.doGetRequestAsync<HhsgSharedApplication[]>(this._apiServer, this._controllerName, 'GetAllApplications');
	}

    public async getAllClients(): Promise<HhsgSharedClient[]> {
		return this._httpService.doGetRequestAsync<HhsgSharedClient[]>(this._apiServer, this._controllerName, 'GetClientsForUser');
	}

    public async getActiveClientsForCorporation(corporationId: number): Promise<HhsgSharedClient[]> {
		return this._httpService.doGetRequestAsync<HhsgSharedClient[]>(this._apiServer, this._controllerName, `GetActiveClientsForCorporation/${corporationId}`);
	}

	public async getAllClientsForCorporation(corporationId: number): Promise<HhsgSharedClient[]> {
		return this._httpService.doGetRequestAsync<HhsgSharedClient[]>(this._apiServer, this._controllerName, `GetAllClientsForCorporation/${corporationId}`);
	}

    public async getClient(clientId: number): Promise<HhsgSharedClient> {
		return this._httpService.doGetRequestAsync(this._apiServer, this._controllerName, `GetByIdWithRelatedEntitiesUntracked/${clientId}`);
	}

    public async getClientInfoForApplication(corporationId: number, appAbbreviation: string): Promise<HhsgSharedCorporationClientApplicationInfo[]>	{
		return this._httpService.doGetRequestAsync<HhsgSharedCorporationClientApplicationInfo[]>(
            this._apiServer, 
            this._controllerName, 
            `GetCorporationClientInfoForApplication/${corporationId}/${appAbbreviation}`);
	}

	public getItemList(includeInactive: boolean, applicationAbbreviation: string, filterByCurrentApplication: boolean,filteredClientIds: number[]): Observable<CustomStore> {
		if(filteredClientIds === undefined){
			filteredClientIds = [];
		}
		return defer(() => this._aspDataStoreService.createASPDataStore({ 
            loadUrl: `${ this._apiServer }/${ this._controllerName }/GetClientList/${includeInactive}/${applicationAbbreviation}/${filterByCurrentApplication}/${ filteredClientIds.length> 0 ? JSON.stringify(filteredClientIds): ''}` 
        }));
	}

    public getOrganizationIdFromClient(client: HhsgSharedClient): number 	{
		if (!client || !client.clientApplicationModels || client.clientApplicationModels.length == 0) {
            return null;
        }

		let studerApp = client.clientApplicationModels.find(app => 
                                                        app.applicationAbbreviation === 'PCM' ||
			                                            app.applicationAbbreviation ===  'LEM' || 
                                                        app.applicationAbbreviation === 'VM');

		if (studerApp) {
            return studerApp.childApplicationClientId;
        }

		return null;
	}
    
    public getStuderOrgId(clientId: number): Promise<number> {
		return this._httpService.doGetRequestAsync(this._apiServer, "Studer", `GetStuderChildApplicationClientId/${clientId}`);
	}

    public  getTimezones(): Observable<HhsgSharedTimezone[]> {
		let queryParams = null;
		return this._httpService.doGetRequestWithParams<HhsgSharedTimezone[]>(this._apiServer, 'TimeZone', 'GetTimezones', queryParams);
	}
    
    public async upsert(updateClientRequest: FormData): Promise<HhsgSharedClient> {
		return await this._httpService.doMultiPartPostRequestAsync<HhsgSharedClient>(this._apiServer, this._controllerName, 'UpsertClient', updateClientRequest);
	}

	async getAllUsersByClientId(clientId: number,applicationAbbreviation: string): Promise<User[]> {
		return await this._httpService.doGetRequestAsync<User[]>(this._apiServer,'ClientAdmin',
		`GetAllUsersByClientId/${clientId}/${applicationAbbreviation}`);
	}

	public announceActiveClient(client:HhsgSharedClient) {
		this.activeClientSource.next(client);
		this.activeClient = client;
	}

	public announceClientAdvancedFiltersClicked(hsgSharedEllipseSelectOption: HhsgSharedEllipseSelectOption){
		this.clientAdvancedFiltersClickedSource.next(hsgSharedEllipseSelectOption);
	}
}
